import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { DataTableColumnSelector } from '@/components/data-display/data-table-column-selector'
import { Pagination } from '@/components/data-display/pagination'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { SearchInput } from '@/components/forms/search-input'
import { Select } from '@/components/forms/select'
import { FilterBar } from '@/components/layout/filter-bar'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import actionDisableIconUrl from '@/assets/action-menu/disable.svg'
import actionEnableIconUrl from '@/assets/action-menu/enable.svg'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import actionResetPasswordIconUrl from '@/assets/action-menu/reset-password.svg'

import { AdminAccountCreateModal } from './account-create-modal'
import { AdminAccountDrawer } from './account-drawer'
import { AdminAccountResetPasswordModal } from './account-reset-password-modal'
import { AdminTemporaryPasswordModal } from './temporary-password-modal'
import {
  listAdminAccounts,
  updateAdminAccount,
  type AdminAccount,
  type AdminAccountsParams,
} from './api'

type AdminStatusFilter = NonNullable<AdminAccountsParams['status']>
type AdminAccountSortField = 'created_at' | 'last_active_at'
type AccountSortOrder = 'asc' | 'desc'

interface TemporaryPasswordResult {
  email: string
  generated_password?: string
  title?: string
  titleHelp?: string
}

const defaultPageSize = 10
const defaultAdminStatusFilter: AdminStatusFilter = 'active'
const defaultAdminAccountColumnIds = [
  'name',
  'email',
  'role',
  'status',
  'created-at',
  'last-active-at',
  'actions',
]

function roleLabel(role: string, t: (key: string) => string) {
  if (role === 'super_admin') {
    return t('adminAccounts.role.superAdmin')
  }

  if (role === 'admin') {
    return t('adminAccounts.role.admin')
  }

  if (role === 'viewer') {
    return t('adminAccounts.role.viewer')
  }

  return role.trim() ? role : t('adminAccounts.role.unknown')
}

function statusLabel(status: AdminAccount['status'], t: (key: string) => string) {
  return t(`adminAccounts.status.${status}`)
}

export function AdminAccountsPage() {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<AdminStatusFilter>(defaultAdminStatusFilter)
  const [sortField, setSortField] = useState<AdminAccountSortField>('created_at')
  const [sortOrder, setSortOrder] = useState<AccountSortOrder>('desc')
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>(defaultAdminAccountColumnIds)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [managingAdmin, setManagingAdmin] = useState<AdminAccount | null>(null)
  const [resetPasswordAdmin, setResetPasswordAdmin] = useState<AdminAccount | null>(null)
  const [createAdminOpen, setCreateAdminOpen] = useState(false)
  const [temporaryPasswordAdmin, setTemporaryPasswordAdmin] =
    useState<TemporaryPasswordResult | null>(null)

  const statusOptions = useMemo(
    () => [
      {
        label: t('common.allStatuses'),
        value: '',
      },
      {
        label: t('adminAccounts.status.active'),
        value: 'active',
      },
      {
        label: t('adminAccounts.status.banned'),
        value: 'banned',
      },
    ],
    [t],
  )
  const accountsQuery = useQuery({
    queryFn: () =>
      listAdminAccounts({
        order: sortOrder,
        orderBy: sortField,
        page,
        pageSize,
        search,
        status: statusFilter,
      }),
    queryKey: [
      'admin-settings-admin-accounts',
      {
        page,
        pageSize,
        search,
        sortField,
        sortOrder,
        statusFilter,
      },
    ],
  })

  const showOperationFeedback = (tone: 'error' | 'success', message: string) => {
    showFeedback({
      message,
      tone,
    })
  }
  const statusMutation = useMutation({
    mutationFn: ({ adminId, status }: { adminId: string; status: 'active' | 'banned' }) =>
      updateAdminAccount({ adminId, status }),
    onError: () => showOperationFeedback('error', t('errors.submitFailed')),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-settings-admin-accounts'] })
      showOperationFeedback('success', t('success.updateStatus'))
    },
  })
  const rows = accountsQuery.data?.items ?? []
  const total = accountsQuery.data?.total ?? 0
  const visibleColumnIdSet = useMemo(() => new Set(visibleColumnIds), [visibleColumnIds])
  const toggleSort = useCallback(
    (field: AdminAccountSortField) => {
      const nextOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc'

      setSortField(field)
      setSortOrder(nextOrder)
      setPage(1)
    },
    [sortField, sortOrder],
  )
  const columns = useMemo<DataTableColumn<AdminAccount>[]>(() => {
    const sortDirection = (field: AdminAccountSortField) => (sortField === field ? sortOrder : null)
    const sortActionLabel = (field: AdminAccountSortField, ascKey: string, descKey: string) =>
      sortField === field && sortOrder === 'desc' ? t(ascKey) : t(descKey)

    return [
      {
        header: t('adminAccounts.table.name'),
        hidden: !visibleColumnIdSet.has('name'),
        id: 'name',
        render: (admin) => <span className="font-medium">{admin.name}</span>,
        visibilityLabel: t('adminAccounts.table.name'),
        width: 180,
      },
      {
        header: t('adminAccounts.table.email'),
        hidden: !visibleColumnIdSet.has('email'),
        id: 'email',
        render: (admin) => admin.email || '-',
        visibilityLabel: t('adminAccounts.table.email'),
        width: 260,
      },
      {
        header: t('adminAccounts.table.role'),
        hidden: !visibleColumnIdSet.has('role'),
        id: 'role',
        render: (admin) => roleLabel(admin.role, t),
        visibilityLabel: t('adminAccounts.table.role'),
        width: 180,
      },
      {
        header: t('adminAccounts.table.status'),
        hidden: !visibleColumnIdSet.has('status'),
        id: 'status',
        render: (admin) => (
          <Badge variant={admin.status === 'active' ? 'success' : 'warning'}>
            {statusLabel(admin.status, t)}
          </Badge>
        ),
        visibilityLabel: t('adminAccounts.table.status'),
        width: 120,
      },
      {
        header: t('adminAccounts.table.createdAt'),
        hidden: !visibleColumnIdSet.has('created-at'),
        id: 'created-at',
        render: (admin) => formatDateTime(admin.created_at),
        sort: {
          direction: sortDirection('created_at'),
          label: sortActionLabel(
            'created_at',
            'adminAccounts.sort.createdAtAsc',
            'adminAccounts.sort.createdAtDesc',
          ),
          onToggle: () => toggleSort('created_at'),
        },
        visibilityLabel: t('adminAccounts.table.createdAt'),
        width: 180,
      },
      {
        header: t('adminAccounts.table.lastActiveAt'),
        hidden: !visibleColumnIdSet.has('last-active-at'),
        id: 'last-active-at',
        render: (admin) => formatDateTime(admin.last_active_at),
        sort: {
          direction: sortDirection('last_active_at'),
          label: sortActionLabel(
            'last_active_at',
            'adminAccounts.sort.lastActiveAtAsc',
            'adminAccounts.sort.lastActiveAtDesc',
          ),
          onToggle: () => toggleSort('last_active_at'),
        },
        visibilityLabel: t('adminAccounts.table.lastActiveAt'),
        width: 180,
      },
      {
        align: 'center',
        header: t('adminAccounts.table.actions'),
        hidden: !visibleColumnIdSet.has('actions'),
        id: 'actions',
        pin: 'right',
        render: (admin) => (
          <ActionMenu
            ariaLabel={admin.name}
            items={[
              {
                iconSrc: actionEditIconUrl,
                key: 'manage',
                label: t('actions.manage'),
                onSelect: () => setManagingAdmin(admin),
              },
              {
                iconSrc: actionResetPasswordIconUrl,
                key: 'reset-password',
                label: t('actions.resetPassword'),
                onSelect: () => setResetPasswordAdmin(admin),
              },
              {
                iconSrc: admin.status === 'active' ? actionDisableIconUrl : actionEnableIconUrl,
                key: admin.status === 'active' ? 'disable' : 'enable',
                label: admin.status === 'active' ? t('actions.disable') : t('actions.enable'),
                onSelect: () =>
                  statusMutation.mutate({
                    adminId: admin.id,
                    status: admin.status === 'active' ? 'banned' : 'active',
                  }),
                variant: admin.status === 'active' ? 'danger' : 'success',
              },
            ]}
          />
        ),
        visibilityLabel: t('adminAccounts.table.actions'),
        width: 90,
      },
    ]
  }, [sortField, sortOrder, statusMutation, t, toggleSort, visibleColumnIdSet])
  const columnSelectorOptions = useMemo(
    () =>
      columns.map((column) => ({
        id: column.id,
        label: column.visibilityLabel ?? column.id,
      })),
    [columns],
  )

  return (
    <AdminShellPage activePath="/admin/settings/admins">
      <section
        aria-label={t('adminAccounts.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden"
        data-testid="admin-accounts-workspace"
      >
        <PageHeader
          actions={
            <>
              <DataTableColumnSelector
                aria-label={t('adminAccounts.columnSelector.label')}
                onValueChange={setVisibleColumnIds}
                options={columnSelectorOptions}
                value={visibleColumnIds}
              >
                {t('adminAccounts.columnSelector.trigger')}
              </DataTableColumnSelector>
              <Button onClick={() => setCreateAdminOpen(true)} size="sm">
                <PlusIcon />
                {t('actions.createAdmin')}
              </Button>
            </>
          }
          description={t('adminAccounts.description')}
          title={t('adminAccounts.title')}
        />
        <FilterBar>
          <SearchInput
            aria-label={t('adminAccounts.searchLabel')}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder={t('adminAccounts.searchPlaceholder')}
            value={search}
          />
          <Select
            aria-label={t('common.allStatuses')}
            onValueChange={(value) => {
              setStatusFilter(value as AdminStatusFilter)
              setPage(1)
            }}
            options={statusOptions}
            value={statusFilter}
          />
        </FilterBar>
        <div className="min-h-0 flex-1 overflow-auto px-5 py-5 lg:px-[30px]">
          {accountsQuery.isError ? (
            <ErrorState
              action={
                <Button
                  className="mt-3"
                  onClick={() => {
                    void accountsQuery.refetch()
                  }}
                  size="sm"
                  variant="secondary"
                >
                  {t('actions.retry')}
                </Button>
              }
              description={t('errors.loadDescription')}
              title={t('errors.loadTitle')}
            />
          ) : (
            <>
              <DataTable
                columns={columns}
                empty={t('adminAccounts.empty')}
                getRowKey={(admin) => admin.id}
                isLoading={accountsQuery.isLoading}
                rows={rows}
              />
              <Pagination
                disabled={accountsQuery.isFetching}
                labels={{
                  next: t('pagination.next'),
                  pageSize: (size) => t('pagination.pageSize', { count: size }),
                  previous: t('pagination.previous'),
                }}
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize)
                  setPage(1)
                }}
                page={page}
                pageSize={pageSize}
                total={total}
              />
            </>
          )}
        </div>
        {managingAdmin ? (
          <AdminAccountDrawer
            key={managingAdmin.id}
            admin={managingAdmin}
            onOpenChange={(open) => {
              if (!open) {
                setManagingAdmin(null)
              }
            }}
            open={Boolean(managingAdmin)}
          />
        ) : null}
        {resetPasswordAdmin ? (
          <AdminAccountResetPasswordModal
            key={resetPasswordAdmin.id}
            admin={resetPasswordAdmin}
            onClose={() => setResetPasswordAdmin(null)}
            onReset={(result) =>
              setTemporaryPasswordAdmin({
                ...result,
                titleHelp: t('temporaryPassword.resetHelp'),
                title: t('temporaryPassword.resetTitle'),
              })
            }
            open={Boolean(resetPasswordAdmin)}
          />
        ) : null}
        <AdminAccountCreateModal
          onClose={() => setCreateAdminOpen(false)}
          onCreated={(result) => setTemporaryPasswordAdmin(result)}
          open={createAdminOpen}
        />
        {temporaryPasswordAdmin ? (
          <AdminTemporaryPasswordModal
            email={temporaryPasswordAdmin.email}
            onClose={() => setTemporaryPasswordAdmin(null)}
            open={Boolean(temporaryPasswordAdmin)}
            password={temporaryPasswordAdmin.generated_password}
            title={temporaryPasswordAdmin.title}
            titleHelp={temporaryPasswordAdmin.titleHelp}
          />
        ) : null}
      </section>
    </AdminShellPage>
  )
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}
